import { useState } from "react";
import { useApi } from "../hooks/useApi";
import { useCphContext } from "../hooks/useCphContext";
import { useCphDataContext } from "../hooks/useCphDataContext";
import {
  getUri_GetAll,
  getUri_GetIssue,
  getUri_AddComment,
  getUri_AddFile,
  getUri_DeleteFile,
  getUri_AssignStaff,
  getUri_ModifyTitle,
  getUri_CompleteIssue,
  getUri_CloseIssue,
  getUri_SetPriority
} from "./ApiUriService";
import { IIssue } from "../types/interfaces/IIssue";
import { IFileDto } from "../types/interfaces/IFileDto";

// This method is used on-demand to find and set the selected issue based on issueKey
// No return value; this function sets state only
export function findAndSetSelectedIssue(
  issuesList: any[],
  issueKey: string
) {
  const { setSelectedIssue } = useCphDataContext();
  if (!Array.isArray(issuesList)) return;
  const found = issuesList.find(issue => issue.issueKey === issueKey);
  if (found) {
    setSelectedIssue(found);
  }
}

// This method is used on-demand to refresh the issues list and the selected issue (if any)
// This is utilized following any operation that modifies an issue and issueslist (e.g., adding a comment, adding a file, etc.)
// No return value; this function sets state only
export async function api_GetIssuesList(
  isStaff: boolean,
  customerKey: string,
  setIssuesList: (issues: IIssue[] | null) => void,
  selectedIssue: IIssue | null,
  setOriginalIssuesList: (issues: IIssue[] | null) => void,
  setFilteredIssuesList: (issues: IIssue[] | null) => void,
  setSelectedIssue: (issue: IIssue | null) => void,
  setIssue: (issue: IIssue | null) => void): Promise<void> {
  const url = getUri_GetAll(isStaff, customerKey);
  try {
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) throw new Error('Failed to fetch issues list');
    const data: IIssue[] = await response.json();
    if (Array.isArray(data)) {
      setOriginalIssuesList(data);
      setFilteredIssuesList(data);
      setIssuesList(data);
      if (selectedIssue?.issueKey) {
        const found = data.find(issue => issue.issueKey === selectedIssue.issueKey);
        setIssue(found || null);
        setSelectedIssue(found || null);
      } else {
        setSelectedIssue(null);
      }
    }
  } catch (err) {
    // Optionally handle error (e.g., set error state)
    console.error('api_GetIssuesList error:', err);
  }
}

// TBD: This method is used on-demand to get a single issue by its key and set it in context
// No return value; this function sets state only
export function api_GetSingleIssue(
  issueKey: string,
  options?: RequestInit,
  deps: any[] = []
): void {
  const { setIssue } = useCphContext();
  const url = getUri_GetIssue(issueKey);
  const method = 'GET';
  const { data } = useApi<IIssue>(url, options, deps, method);
  if (data && !Array.isArray(data)) {
    setIssue(data);
  }
}

// This method is used on-demand to add a comment to a specific issue
// After adding the comment, it refreshes the issues list and selected issue (if any)
// No return value; this function sets state only

export function useAddComment() {
  const { customerKey, isStaff, setIssuesList, setIssue } = useCphContext();
  const { selectedIssue, setOriginalIssuesList, setFilteredIssuesList, setSelectedIssue } = useCphDataContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const addComment = async (issueKey: string, comment: string, staffOnlyFlag: boolean, options?: RequestInit) => {
    console.log('[useAddComment] addComment called', { issueKey, comment, staffOnlyFlag, options });
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const url = getUri_AddComment(issueKey, customerKey!);
      const method = 'POST';
      const requestOptions: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(options?.headers || {})
        },
        body: JSON.stringify({ comment, staffOnlyFlag }),
        ...options,
      };
      console.log('[useAddComment] Fetching', url, requestOptions);
      const response = await fetch(url, requestOptions);
      console.log('[useAddComment] Fetch response', response);
      if (!response.ok) throw new Error('Failed to add comment');
      setSuccess(true);
      // Get all required context/state for api_GetIssuesList
      await api_GetIssuesList(
        isStaff,
        customerKey!,
        setIssuesList,
        selectedIssue!,
        setOriginalIssuesList,
        setFilteredIssuesList,
        setSelectedIssue,
        setIssue
      );
      console.log('[useAddComment] api_GetIssuesList completed');
    } catch (err: any) {
      console.error('[useAddComment] Error', err);
      setError(err);
    } finally {
      setLoading(false);
      console.log('[useAddComment] addComment finished');
    }
  };

  return { addComment, loading, error, success };
}

// No return value; this function sets state only
export function useAddFile() {
  const { customerKey, isStaff, setIssuesList, setIssue } = useCphContext();
  const { selectedIssue, setOriginalIssuesList, setFilteredIssuesList, setSelectedIssue } = useCphDataContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const addFile = async (issueKey: string, reporterAapId: number, file: File, options?: RequestInit) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const url = getUri_AddFile(issueKey, reporterAapId, customerKey!, isStaff!);
      const method = 'POST';
      const formData = new FormData();
      formData.append('formFile', file); // this name must match the parameter name in the MVC controller method

      const requestOptions: RequestInit = {
        method,
        body: formData,
        ...options,
        // Do NOT set Content-Type; browser will set it automatically for FormData
      };
      const response = await fetch(url, requestOptions);
      if (!response.ok) throw new Error('Failed to add file');
      setSuccess(true);
      // Get all required context/state for api_GetIssuesList

      await api_GetIssuesList(
        isStaff,
        customerKey!,
        setIssuesList,
        selectedIssue!,
        setOriginalIssuesList,
        setFilteredIssuesList,
        setSelectedIssue,
        setIssue
      );
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { addFile, loading, error, success };
}

// No return value; this function sets state only
export function useDeleteFile() {
  const { isStaff, customerKey, setIssuesList, setIssue } = useCphContext();
  const { selectedIssue, setOriginalIssuesList, setFilteredIssuesList, setSelectedIssue } = useCphDataContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteFile = async (fileDto: IFileDto, options?: RequestInit) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const url = getUri_DeleteFile(customerKey!);
      const method = 'POST';
      const requestOptions: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(options?.headers || {})
        },
        body: JSON.stringify(fileDto),
        ...options,
      };
      const response = await fetch(url, requestOptions);
      if (!response.ok) throw new Error('Failed to delete file');
      setSuccess(true);
      // Get all required context/state for api_GetIssuesList
      await api_GetIssuesList(
        isStaff,
        customerKey!,
        setIssuesList,
        selectedIssue!,
        setOriginalIssuesList,
        setFilteredIssuesList,
        setSelectedIssue,
        setIssue
      );
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { deleteFile, loading, error, success };
}

// No return value; this function sets state only
export function useAssignStaff() {
  const { isStaff, customerKey, setIssuesList, setIssue } = useCphContext();
  const { selectedIssue, setOriginalIssuesList, setFilteredIssuesList, setSelectedIssue } = useCphDataContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const assignStaff = async (aapId: string, issueKey: string, options?: RequestInit) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const url = getUri_AssignStaff(issueKey, aapId!);
      const method = 'POST';
      const requestOptions: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(options?.headers || {})
        },
        ...options,
      };
      const response = await fetch(url, requestOptions);
      if (!response.ok) throw new Error('Failed to assign staff');
      setSuccess(true);
      // Get all required context/state for api_GetIssuesList
      await api_GetIssuesList(
        isStaff,
        customerKey!,
        setIssuesList,
        selectedIssue!,
        setOriginalIssuesList,
        setFilteredIssuesList,
        setSelectedIssue,
        setIssue
      );
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { assignStaff, loading, error, success };
}

// No return value; this function sets state only
export function useModifyStaffTitle() {
  const { isStaff, customerKey, setIssuesList, setIssue } = useCphContext();
  const { selectedIssue, setOriginalIssuesList, setFilteredIssuesList, setSelectedIssue } = useCphDataContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const modifyStaffTitle = async (issueKey: string, title: string, options?: RequestInit) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const url = getUri_ModifyTitle(issueKey);
      const method = 'POST';
      const requestOptions: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(options?.headers || {})
        },
        body: JSON.stringify({ title }),
        ...options,
      };
      const response = await fetch(url, requestOptions);
      if (!response.ok) throw new Error('Failed to modify staff note');
      setSuccess(true);
      // Get all required context/state for api_GetIssuesList
      await api_GetIssuesList(
        isStaff,
        customerKey!,
        setIssuesList,
        selectedIssue!,
        setOriginalIssuesList,
        setFilteredIssuesList,
        setSelectedIssue,
        setIssue
      );
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { modifyStaffTitle, loading, error, success };
}

// No return value; this function sets state only
export function useCompleteIssue() {
  const { isStaff, customerKey, setIssuesList, setIssue } = useCphContext();
  const { selectedIssue, setOriginalIssuesList, setFilteredIssuesList, setSelectedIssue } = useCphDataContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const completeIssue = async (issueKey: string, options?: RequestInit) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const url = getUri_CompleteIssue(issueKey, customerKey!);
      const method = 'POST';
      const requestOptions: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(options?.headers || {})
        },
        ...options,
      };
      const response = await fetch(url, requestOptions);
      if (!response.ok) throw new Error('Failed to close issue');
      setSuccess(true);
      // Get all required context/state for api_GetIssuesList
      await api_GetIssuesList(
        isStaff,
        customerKey!,
        setIssuesList,
        selectedIssue!,
        setOriginalIssuesList,
        setFilteredIssuesList,
        setSelectedIssue,
        setIssue
      );
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  return { completeIssue, loading, error, success };
}

// No return value; this function sets state only
export function useCloseIssue() {
  const { isStaff, customerKey, setIssuesList, setIssue } = useCphContext();
  const { selectedIssue, setOriginalIssuesList, setFilteredIssuesList, setSelectedIssue } = useCphDataContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const closeIssue = async (issueKey: string, options?: RequestInit) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const url = getUri_CloseIssue(issueKey, customerKey!);
      const method = 'POST';
      const requestOptions: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(options?.headers || {})
        },
        ...options,
      };
      const response = await fetch(url, requestOptions);
      if (!response.ok) throw new Error('Failed to close issue');
      setSuccess(true);
      // Get all required context/state for api_GetIssuesList
      await api_GetIssuesList(
        isStaff,
        customerKey!,
        setIssuesList,
        selectedIssue!,
        setOriginalIssuesList,
        setFilteredIssuesList,
        setSelectedIssue,
        setIssue
      );
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { closeIssue, loading, error, success };
}

// No return value; this function sets state only
export function useSetIssuePriority() {
  const { isStaff, customerKey, setIssuesList, setIssue } = useCphContext();
  const { selectedIssue, setOriginalIssuesList, setFilteredIssuesList, setSelectedIssue } = useCphDataContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const setIssuePriority = async (issueKey: string, priorityKey: string, options?: RequestInit) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const url = getUri_SetPriority(issueKey, priorityKey);
      const method = 'POST';
      const requestOptions: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(options?.headers || {})
        },
        ...options,
      };
      const response = await fetch(url, requestOptions);
      if (!response.ok) throw new Error('Failed to set issue priority');
      setSuccess(true);
      // Get all required context/state for api_GetIssuesList
      await api_GetIssuesList(
        isStaff,
        customerKey!,
        setIssuesList,
        selectedIssue!,
        setOriginalIssuesList,
        setFilteredIssuesList,
        setSelectedIssue,
        setIssue
      );
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { setIssuePriority, loading, error, success };
}
